/**
 * Merchant balance-query list (PR-6a — operational observability).
 *
 * Today's only producer is M-Pesa Daraja's AccountBalance API. State
 * is derived: dispatched / stale / settled / failed.
 */
import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Wallet, RotateCw } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import {
  getBalanceQueries,
  type BalanceQueryRow,
  type BalanceQueryState,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

const STATES: readonly BalanceQueryState[] = [
  "dispatched",
  "settled",
  "failed",
  "stale",
] as const;

const STATE_VARIANT: Record<BalanceQueryState, BadgeProps["variant"]> = {
  dispatched: "info",
  settled: "success",
  failed: "destructive",
  stale: "warning",
};

export function BalanceQueriesListPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const state = (params.get("state") as BalanceQueryState | null) ?? undefined;

  const query = useQuery({
    queryKey: ["balance-queries", { state }] as const,
    queryFn: () => getBalanceQueries({ state }),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load balance queries", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error]);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const hasFilters = Boolean(state);
  const isZeroGrantTenant =
    user?.role === "tenant" && user.tenants.length === 0 && !hasFilters;

  const columns = useMemo<ReadonlyArray<DataTableColumn<BalanceQueryRow>>>(
    () => [
      {
        key: "id",
        header: "Query",
        cell: (r) => (
          <code className="font-mono text-xs text-muted-foreground">
            {r.id.slice(0, 14)}…
          </code>
        ),
      },
      {
        key: "tenant",
        header: "Tenant",
        cell: (r) => (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium">{r.tenant.name}</span>
            <Badge variant="muted" className="capitalize">
              {r.tenant.environment}
            </Badge>
          </div>
        ),
      },
      {
        key: "account",
        header: "Provider account",
        cell: (r) => (
          <span className="text-sm">
            {r.provider_account.plugin} · {r.provider_account.name}
          </span>
        ),
      },
      {
        key: "state",
        header: "State",
        cell: (r) => (
          <Badge variant={STATE_VARIANT[r.state]} className="capitalize">
            {r.state}
          </Badge>
        ),
      },
      {
        key: "result_code",
        header: "Result",
        className: "tabular-nums",
        cell: (r) => (
          <span className="text-sm">{r.result_code ?? "—"}</span>
        ),
      },
      {
        key: "created_at",
        header: "Dispatched",
        className: "text-muted-foreground",
        cell: (r) => (
          <span className="whitespace-nowrap text-xs">
            {new Date(r.created_at).toLocaleString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Operations / Money"
        title="Balance queries"
        description="Outstanding and settled merchant-balance queries. M-Pesa Daraja is the only producer today."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
            Refresh
          </Button>
        }
      />

      <Toolbar
        filters={
          <FilterGroup label="State">
            <FilterChip
              active={state === undefined}
              onClick={() => setFilter("state", null)}
            >
              All
            </FilterChip>
            {STATES.map((s) => (
              <FilterChip
                key={s}
                active={state === s}
                onClick={() => setFilter("state", s)}
              >
                {s}
              </FilterChip>
            ))}
          </FilterGroup>
        }
      />

      <DataTable<BalanceQueryRow>
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/balance-queries/${r.id}`)}
        emptyState={
          isZeroGrantTenant ? (
            <EmptyState
              icon={Wallet}
              title="No tenants granted yet"
              description="Balance queries appear here once a super-admin grants you access to a tenant."
            />
          ) : (
            <EmptyState
              icon={Wallet}
              title="No balance queries match these filters"
              description="Try clearing a filter. Queries appear here as your platform dispatches AccountBalance requests."
              action={
                hasFilters ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/balance-queries">Clear filters</Link>
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
    </>
  );
}

export default BalanceQueriesListPage;
